import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useHistory } from "react-router";
import { Button, IconButton, TextField, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import Select from "react-select";
import { useDropzone } from "react-dropzone";
import parseRequirements from "../../utils/parseRequirements";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Loader from "../../components/Loader";
import {
  FormPageShell,
  FormPageCard,
  FormPageHeader,
  FormActions,
} from "../../components/ListPage";

import Swal from "sweetalert2";
import Toast from "../../utils/Toast";

import useRequirements from "../../queries/addon/requirements";
import useCreateAddon from "../../mutation/addon/createAddon";
import useCreateAudit from "../../mutation/audit/createAudit";
import useProperties from "../../queries/property/getProperties";
import useRoomTypes from "../../queries/roomType/getRoomTypeLinks";

const errorTextSx = {
  mx: 1.75,
  mt: 0.375,
  fontSize: "0.75rem",
  fontWeight: 400,
  color: "secondary.main",
};

const imageLabelSx = {
  fontSize: 16,
  fontWeight: "bold",
};

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  name_chinese: yup.string().required("Name is required"),
  price: yup
    .number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(0, "Price cannot be negative"),
  price_hk: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Price HK cannot be negative"),
  requirement: yup.object().required("A Requirement must be selected."),
});

const NewAddon = (props) => {
  const history = useHistory();

  const [requirementsResponse, reset] = useRequirements();
  const [auditResponse, setAuditData] = useCreateAudit();
  const [propertiesResponse] = useProperties();
  const [roomTypesResponse] = useRoomTypes();

  const [requirementOptions, setRequirementOptions] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);

  const [uploadedFile, setFile] = useState();
  const [fileError, setFileError] = useState();

  const { control, register, handleSubmit, setValue, errors } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (requirementsResponse.data) {
      let requirements = requirementsResponse.data.requirements;
      if (requirements && requirements.length > 0) {
        setRequirementOptions(parseRequirements(requirements));
      }
    }
  }, [requirementsResponse]);

  useEffect(() => {
    if (propertiesResponse.data) {
      const options = propertiesResponse.data.map((prop) => ({
        value: prop.id,
        label: prop.name,
      }));
      setPropertyOptions(options);
    }
  }, [propertiesResponse]);

  useEffect(() => {
    if (roomTypesResponse.data) {
      const options = roomTypesResponse.data.map((roomType) => ({
        value: roomType.id,
        label: roomType.name || roomType.display_name || `Room Type ${roomType.id}`,
      }));
      setRoomTypeOptions(options);
    }
  }, [roomTypesResponse]);

  const goBack = () => {
    window.location.href = "/dashboard/addons";
  };

  const removeFile = () => {
    setFile(null);
    setDropText("Drag-and-drop or select a file to upload.");
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      setFileError(null);
      setFile(file);

      setDropText(file.name);
    });
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    maxFiles: 1,
    noClick: uploadedFile != null,
    accept: "image/*",
    disabled: auditResponse.loading,
  });

  const [dropText, setDropText] = useState(
    "Drag or select an image to upload."
  );

  const dropzoneSx = useMemo(
    () => ({
      mt: 2.5,
      flex: 1,
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      p: "30px",
      borderWidth: 2,
      borderRadius: "2px",
      borderColor: isDragReject
        ? "#ff1744"
        : isDragAccept
        ? "#00e676"
        : isDragActive
        ? "#2196f3"
        : "#eeeeee",
      borderStyle: "dashed",
      bgcolor: "#fafafa",
      color: "#bdbdbd",
      outline: "none",
      transition: "border .24s ease-in-out",
      fontSize: 20,
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const requirementChange = (value) => {
    if (value != null) {
      setValue("requirement", { label: value.label, value: value.value });
    }
  };

  const propertyChange = (selectedOptions) => {
    setValue("property", selectedOptions || []);
  };

  const roomTypeChange = (selectedOptions) => {
    setValue("room_type", selectedOptions || []);
  };

  var countDecimals = function (value) {
    if (Math.floor(value) === value) return 0;
    if (value.toString().split(".").length > 1) {
      return value.toString().split(".")[1].length || 0;
    } else {
      return 0;
    }
  };

  const onPriceChange = (event) => {
    let value = event.target.value;
    if (countDecimals(value) > 2) {
      setValue("price", Number(value).toFixed(2));
    }
  };

  const onPriceHkChange = (event) => {
    let value = event.target.value;
    if (countDecimals(value) > 2) {
      setValue("price_hk", Number(value).toFixed(2));
    }
  };

  const onSubmit = (values) => {
    console.log(values);
    /*if (!uploadedFile) {
      setFileError("Image not uploaded");
      return;
    }*/
    let data = {
      name: values.name,
      name_chinese: values.name_chinese,
      description: values.description,
      description_chinese: values.description_chinese,
      price: values.price,
      price_hk: values.price_hk != null && values.price_hk !== "" ? values.price_hk : null,
      requirementId: values.requirement.value.toString(),
      requirementName: values.requirement.label,
      property: values.property ? values.property.map((p) => p.value) : [],
      room_type: values.room_type ? values.room_type.map((rt) => rt.value) : [],
    };
    let formData;
    if (uploadedFile) {
      formData = new FormData();
      formData.append("files.newImageData", uploadedFile, uploadedFile.name);
    }
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        let auditData = {
          actionDescription: actionDescription,
          newData: data,
          type: "addon",
          action: "create",
        };
        if (formData) {
          formData.append("data", JSON.stringify(auditData));
          return formData;
        } else {
          return auditData;
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
      }
    });
  };

  useEffect(() => {
    try {
      if (auditResponse.data) {
        Toast.fire({
          icon: "success",
          title: "Add-on audit created",
        });
        history.push("/dashboard/addons", { newAddon: true });
      } else if (auditResponse.error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: auditResponse.error.message,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    }
  }, [auditResponse]);

  const renderContent = () => {
    return (
      <>
        <FormPageHeader
          title="New Add-on"
          onBack={goBack}
          subtitle="Please fill in the required fields."
        />
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          sx={{ width: "100%" }}
        >
          <TextField
            name="name"
            margin="normal"
            fullWidth
            label="Name*"
            variant="outlined"
            inputRef={register}
            error={errors.name != null}
            helperText={errors.name != null ? errors.name.message : null}
            disabled={auditResponse.loading}
          />
          <TextField
            name="name_chinese"
            margin="normal"
            fullWidth
            label="Name (Chinese)*"
            variant="outlined"
            inputRef={register}
            error={errors.name_chinese != null}
            helperText={
              errors.name_chinese != null ? errors.name_chinese.message : null
            }
            disabled={auditResponse.loading}
          />
          <TextField
            name="description"
            margin="normal"
            fullWidth
            multiline={true}
            label="Description"
            variant="outlined"
            inputRef={register}
            error={errors.description != null}
            helperText={
              errors.description != null ? errors.description.message : null
            }
            disabled={auditResponse.loading}
          />
          <TextField
            name="description_chinese"
            margin="normal"
            fullWidth
            multiline={true}
            label="Description (Chinese)"
            variant="outlined"
            inputRef={register}
            error={errors.description_chinese != null}
            helperText={
              errors.description_chinese != null
                ? errors.description_chinese.message
                : null
            }
            disabled={auditResponse.loading}
          />
          <TextField
            name="price"
            margin="normal"
            fullWidth
            label="Amount in AUD*"
            variant="outlined"
            inputRef={register}
            type="number"
            onChange={onPriceChange}
            defaultValue={0}
            error={errors.price != null}
            helperText={errors.price != null ? errors.price.message : null}
            disabled={auditResponse.loading}
          />
          <TextField
            name="price_hk"
            margin="normal"
            fullWidth
            label="Amount HK in AUD (Optional)"
            variant="outlined"
            inputRef={register}
            type="number"
            onChange={onPriceHkChange}
            defaultValue=""
            placeholder="HK/Dundas property price"
            error={errors.price_hk != null}
            helperText={errors.price_hk != null ? errors.price_hk.message : null}
            disabled={auditResponse.loading}
          />
          <Typography component="p" sx={imageLabelSx}>
            Property (Optional)
          </Typography>
          <Typography component="p" sx={{ mb: 0 }}>
            Select one or more properties. If none selected, addon applies to all properties.
          </Typography>
          {propertyOptions.length > 0 && (
            <Box>
              <Controller
                name="property"
                control={control}
                defaultValue={[]}
                render={({ ...props }) => {
                  return (
                    <Select
                      isMulti
                      options={propertyOptions}
                      onChange={propertyChange}
                      disabled={auditResponse.loading}
                      placeholder="Select properties..."
                    />
                  );
                }}
              />
            </Box>
          )}
          <Typography component="p" sx={{ ...imageLabelSx, mt: 2.5 }}>
            Room Type (Optional)
          </Typography>
          <Typography component="p" sx={{ mb: 0 }}>
            Select one or more room types. If none selected, addon applies to all room types.
          </Typography>
          {roomTypeOptions.length > 0 && (
            <Box>
              <Controller
                name="room_type"
                control={control}
                defaultValue={[]}
                render={({ ...props }) => {
                  return (
                    <Select
                      isMulti
                      options={roomTypeOptions}
                      onChange={roomTypeChange}
                      disabled={auditResponse.loading}
                      placeholder="Select room types..."
                    />
                  );
                }}
              />
            </Box>
          )}
          <Typography component="p" sx={imageLabelSx}>
            Image*
          </Typography>
          <Box {...getRootProps()} sx={dropzoneSx}>
            <input {...getInputProps()} />
            <p>{dropText}</p>
            {uploadedFile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  onClick={removeFile}
                  color="secondary"
                  aria-label="Remove Image"
                  disableRipple={true}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
          </Box>
          {fileError && <Box sx={errorTextSx}>{fileError}</Box>}
          <Typography component="p" sx={imageLabelSx}>
            Set to Requirement (from RMS Cloud)
          </Typography>
          {requirementOptions.length > 0 && (
            <Box>
              <Controller
                name="requirement"
                control={control}
                render={({ ...props }) => {
                  return (
                    <Select
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: errors.requirement
                            ? "red"
                            : provided.borderColor,
                        }),
                      }}
                      options={requirementOptions}
                      onChange={requirementChange}
                      disabled={auditResponse.loading}
                    />
                  );
                }}
              />
              {errors.requirement && (
                <Box sx={errorTextSx}>{errors.requirement.message}</Box>
              )}
            </Box>
          )}
          <FormActions>
            <Button
              type="submit"
              size="large"
              variant="contained"
              color="primary"
              disabled={auditResponse.loading}
            >
              Submit
            </Button>
          </FormActions>
        </Box>
      </>
    );
  };

  return (
    <FormPageShell>
      <FormPageCard>
        {(requirementsResponse.loading ||
          propertiesResponse.loading ||
          roomTypesResponse.loading) &&
          !requirementsResponse.data && <Loader />}
        {!requirementsResponse.loading &&
          !propertiesResponse.loading &&
          !roomTypesResponse.loading &&
          renderContent()}
      </FormPageCard>
    </FormPageShell>
  );
};

export default NewAddon;
